import { doApi } from './apiClient';
import type {
  User,
  LoginCredentials,
  RegisterPayload,
  RegisterResponse,
  AuthResponse,
  PaginatedResponse,
  ApiResponse,
  GetAllUsersResponse,
  UserListItem,
  ForgotPasswordPayload,
  ResetPasswordPayload,
} from '../types';
import type { CardAccount, CreditGrant, UserInventoryCard } from '../types/models/userCredit';

export const apiCreateUser = async (userData: RegisterPayload): Promise<RegisterResponse> => {
  const response = await doApi<RegisterResponse>('api/user/registration', 'POST', userData);
  if (response.token) {
    localStorage.setItem('token', response.token);
  }
  return response;
};

export const apiLogUser = async (userData: LoginCredentials): Promise<AuthResponse> => {
  const response = await doApi<AuthResponse>('api/user/login', 'POST', userData);
  if (response.access_token) {
    localStorage.setItem('token', response.access_token);
  }
  return response;
};

export const apiLogoutUser = async (): Promise<ApiResponse<null>> => {
  const response = await doApi<ApiResponse<null>>('api/user/logout', 'GET');
  localStorage.removeItem('token');
  return response;
};

export const apiListGetUserByToken = async (): Promise<User | null> => {
  try {
    return await doApi<User>('api/user', 'GET');
  } catch {
    return null;
  }
};

export const apiListUsersByPage = async (): Promise<PaginatedResponse<User>> => {
  return doApi<PaginatedResponse<User>>('api/user/list', 'GET');
};

export const apiListAllUsers = async (): Promise<UserListItem[]> => {
  const response = await doApi<GetAllUsersResponse>('api/user/v2/all?page=all', 'GET');
  return response.data;
};

export const apiListUsersWithParams = async (
  orderBy: string,
  isOrderDesc: boolean
): Promise<PaginatedResponse<User>> => {
  return doApi<PaginatedResponse<User>>(
    `api/user/list?order=${orderBy}&isOrderDesc=${isOrderDesc}`,
    'GET'
  );
};

export const apiAddAdmin = async (userId: number): Promise<{ message: string }> => {
  return doApi<{ message: string }>(`api/user/v2/${userId}/make-admin`, 'PATCH');
};

export const apiRemoveAdmin = async (userId: number): Promise<{ message: string }> => {
  return doApi<{ message: string }>(`api/user/v2/${userId}/remove-admin`, 'PATCH');
};

export const apiUnlinkOwnWrestler = async (wrestlerId: number): Promise<{ message: string }> => {
  return doApi<{ message: string }>(`api/user/v2/wrestler/${wrestlerId}/unlink-self`, 'PATCH');
};

export const apiAdminUnlinkWrestler = async (wrestlerId: number): Promise<{ message: string }> => {
  return doApi<{ message: string }>(`api/user/v2/wrestler/${wrestlerId}/admin-unlink`, 'PATCH');
};

export const apiAdminAssignWrestlerToUser = async (
  wrestlerId: number,
  userId: number
): Promise<ApiResponse<null>> => {
  return doApi<ApiResponse<null>>(
    `api/user/v2/wrestler/${wrestlerId}/admin-assign`,
    'PATCH',
    { user_id: userId }
  );
};

// Password Reset
export const apiForgotPassword = async (
  payload: ForgotPasswordPayload
): Promise<{ message: string; errors?: Record<string, string[]> }> => {
  return doApi<{ message: string; errors?: Record<string, string[]> }>(
    'api/user/v2/forgot-password',
    'POST',
    payload
  );
};

export const apiResetPassword = async (
  payload: ResetPasswordPayload
): Promise<{ message: string; errors?: Record<string, string[]> }> => {
  return doApi<{ message: string; errors?: Record<string, string[]> }>(
    'api/user/v2/reset-password',
    'POST',
    payload
  );
};

export const apiAssignWrestler = async (
  wrestler: { wrestler_id: number }
): Promise<ApiResponse<{ wrestler: { id: number; name: string } }>> => {
  return doApi<ApiResponse<{ wrestler: { id: number; name: string } }>>(
    'api/user/assignwrestler',
    'POST',
    wrestler
  );
};

// ─── Admin Credits ────────────────────────────────────────────────────────────

export const apiGetCardAccounts = (): Promise<{ data: CardAccount[] }> =>
  doApi('api/card-accounts', 'GET');

export const apiAdjustCredits = (
  userId: number,
  amount: number,
  notes?: string
): Promise<{ message: string }> =>
  doApi(`api/admin/users/${userId}/credits`, 'POST', { amount, notes });

export const apiGetCreditHistory = (
  userId: number
): Promise<CreditGrant[]> =>
  doApi(`api/admin/users/${userId}/credits/history`, 'GET');

export const apiGetUserInventory = (
  userId: number
): Promise<{ data: UserInventoryCard[] }> =>
  doApi(`api/admin/users/${userId}/cards`, 'GET');

export const apiGrantAllCredits = (
  amount: number,
  notes?: string
): Promise<{ message: string; users_updated: number }> =>
  doApi('api/admin/users/grant-all-credits', 'POST', { amount, ...(notes ? { notes } : {}) });
